import { FC, useCallback, useRef, useState } from 'react';

import {
  GiftCouponRedeemStatus,
  GiftCouponShareStatus,
  GiftCouponSummary,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftCsMemo } from '@shared/api/giftCsMemo/giftCsMemo.interface';
import { formatPrice } from '@shared/utils/formatPrice';
import { ShareChannelIcon } from '@service/giftStore/components/ShareChannelIcon';

import { CsMemoButton } from '../csMemo';
import {
  extraRedeemRemaining,
  fmt,
  fmtPhone,
  fmtTime,
  isCouponCancellable,
  isCouponDisposable,
  isExtraRedeemEligible,
  isSpawnCoupon,
  isSpawnPending,
  redeemStatusColor,
  redeemStatusLabel,
  shareChannelLabel,
  shareStatusColor,
  shareStatusLabel,
} from './constants';
import { copyAdminPreviewLink } from '../../utils/adminPreviewLink';
import { GiftItemEmbed } from './types';

interface CouponRowProps {
  coupon: GiftCouponSummary;
  embed: GiftItemEmbed | undefined;
  canCancel: boolean;
  hasCancellableCoupons: boolean;
  disposeMode: boolean;
  isSelected: boolean;
  isDisposeSelected: boolean;
  isPromoting: boolean;
  /** 다른 쿠폰과 같은 카톡방에 공유된 케이스 (cross-coupon overlap) */
  isCrossCouponOverlap?: boolean;
  toggleSelect: (id: string) => void;
  toggleDisposeSelect: (id: string) => void;
  /** 재공유이력 칩 클릭 → 재공유 이력 모달 오픈 */
  onOpenShareHistory: (coupon: GiftCouponSummary) => void;
  handlePromoteToReissued: (couponId: string) => Promise<void>;
  onIssueRefund?: (couponId: string) => void;
  onIssueRenewal?: (couponId: string) => void;
  onCancelIssue?: (couponId: string) => void;
  /** 카톡 카운트 뱃지 클릭 → 카카오 공유 콜백 모달 오픈 */
  onOpenKakaoShareCallbacks?: (coupon: GiftCouponSummary) => void;
  isIssuing?: boolean;
  /** 공유상태 뱃지 클릭 → 미전달(NotShared)로 되돌리기 */
  onResetShareStatus?: (couponId: string) => Promise<void>;
  isResettingShare?: boolean;
  /** 제공 시 "수신번호"(확인번호) 셀을 인라인 편집 가능하게 한다 — 저장 후 새로고침하지 않음 */
  onUpdateAdditionalPhone?: (couponId: string, newPhone: string) => Promise<boolean>;
  /** 구매일 / 만료·교환일 컬럼 숨김 (배너 등 컴팩트 표시용) */
  hideDates?: boolean;
  /** 제공 시 "중복허용"(추가교환) 컬럼 렌더 + 클릭 시 모달 오픈 */
  onOpenExtraRedeem?: (coupon: GiftCouponSummary) => void;
  // ─── 스폰(추가교환) 쿠폰 추가결제 대기건 처리 ───
  /** 입금(추가결제) 모달 오픈 */
  onSpawnPay?: (coupon: GiftCouponSummary) => void;
  /** 대체발송 모달 오픈 */
  onSpawnFulfill?: (coupon: GiftCouponSummary) => void;
  /** 신청 취소 (스폰 쿠폰 삭제) */
  onSpawnCancel?: (couponId: string) => void | Promise<void>;
  /** 취소 처리 중인 스폰 쿠폰 id */
  cancelingSpawnId?: string | null;
  /** 이 스폰 쿠폰이 중복 생성된 원본 쿠폰 정보 (spawnedFrom 해석 결과) */
  spawnOrigin?: { code: string; seq: number } | null;
  // ─── CS 응대 메모 (전달 시 "CS메모" 컬럼 렌더) ───
  /** 이 쿠폰 단위 메모 — 주문 축 1회 조회분에서 갈라 받은 것 */
  csMemos?: GiftCsMemo[];
  csMemoLoading?: boolean;
  csMemoUnavailable?: boolean;
  /** CS메모 컬럼 표시 여부 (표 헤더와 맞춰서 전달) */
  showCsMemo?: boolean;
  /** 메모장 부제목 (주문자 등) */
  csMemoSubtitle?: string;
  /** 주문검색 "쿠폰 찾기"에서 짚어 들어온 쿠폰 — 행 강조 */
  isHighlighted?: boolean;
}

export const CouponRow: FC<CouponRowProps> = ({
  coupon: c,
  embed,
  canCancel,
  hasCancellableCoupons,
  disposeMode,
  isSelected,
  isDisposeSelected,
  isPromoting,
  isCrossCouponOverlap,
  toggleSelect,
  toggleDisposeSelect,
  onOpenShareHistory,
  handlePromoteToReissued,
  onIssueRefund,
  onIssueRenewal,
  onCancelIssue,
  onOpenKakaoShareCallbacks,
  isIssuing,
  onResetShareStatus,
  isResettingShare,
  onUpdateAdditionalPhone,
  hideDates,
  onOpenExtraRedeem,
  onSpawnPay,
  onSpawnFulfill,
  onSpawnCancel,
  cancelingSpawnId,
  spawnOrigin,
  csMemos,
  csMemoLoading,
  csMemoUnavailable,
  showCsMemo,
  csMemoSubtitle,
  isHighlighted,
}) => {
  const cancellable = isCouponCancellable(c);
  const disposable = isCouponDisposable(c);
  const isCanceled = c.redeemStatus === GiftCouponRedeemStatus.Canceled;
  const isDisposed = c.redeemStatus === GiftCouponRedeemStatus.Disposed;
  const isInactive = isCanceled || isDisposed;

  // 스폰(추가교환) 쿠폰: 중복 공유 케이스라 카톡공유/공유채널/카톡전송·확인일시가 없다.
  // 빈 칸을 활용해 (1) 어떤 원본에서 파생됐는지 표시, (2) 미정산 대기건이면 입금/대체발송
  // 버튼 노출, (3) 취소는 정산 여부와 무관하게 "중복허용" 컬럼에 항상 노출(백엔드가 가드).
  const isSpawn = isSpawnCoupon(c);
  const spawnPending = isSpawnPending(c);
  const showSpawnActions = spawnPending && (!!onSpawnPay || !!onSpawnFulfill);

  return (
    <tr
      id={`gift-coupon-row-${c._id}`}
      className={`${isHighlighted ? 'ring-2 ring-inset ring-indigo-500' : ''} ${
        isInactive
          ? 'bg-gray-50 opacity-60'
          : isDisposeSelected
          ? 'bg-slate-100'
          : isSelected
          ? 'bg-red-50'
          : c.isExtraPaid
          ? 'bg-violet-50/50 hover:bg-violet-50'
          : c.isAdditional || c.isReissued
          ? 'bg-amber-50/40 hover:bg-amber-50'
          : 'hover:bg-gray-50'
      }`}
    >
      {canCancel && hasCancellableCoupons && !disposeMode && (
        <td className="whitespace-nowrap px-3 py-1.5">
          {cancellable ? (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleSelect(c._id)}
              className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
            />
          ) : (
            <span className="text-gray-300">-</span>
          )}
        </td>
      )}
      {disposeMode && (
        <td className="whitespace-nowrap px-3 py-1.5">
          {disposable ? (
            <input
              type="checkbox"
              checked={isDisposeSelected}
              onChange={() => toggleDisposeSelect(c._id)}
              className="h-4 w-4 rounded border-slate-300 text-slate-600 focus:ring-slate-500"
            />
          ) : (
            <span className="text-gray-300">-</span>
          )}
        </td>
      )}
      <td className="whitespace-nowrap px-3 py-1.5 text-gray-400">{c.seq ?? ''}</td>
      <td className="whitespace-nowrap px-3 py-1.5">
        {c.isReissued ? (
          <span className="inline-flex rounded-full bg-teal-100 px-1.5 py-0.5 text-[10px] font-bold leading-tight text-teal-700">
            재발급
          </span>
        ) : c.isAdditional ? (
          <div className="flex items-center gap-1">
            <span className="inline-flex rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold leading-tight text-amber-700">
              재발행
            </span>
            {c.redeemStatus === GiftCouponRedeemStatus.Available && (
              <button
                onClick={() => handlePromoteToReissued(c._id)}
                disabled={isPromoting}
                className="inline-flex rounded bg-teal-50 px-1 py-0.5 text-[9px] font-semibold text-teal-600 hover:bg-teal-100 disabled:opacity-50"
                title="재발행 → 재발급 승격 (isAdditional → isReissued)"
              >
                승격
              </button>
            )}
          </div>
        ) : c.isExtraPaid ? (
          <div className="flex flex-col items-start gap-0.5">
            <span className="inline-flex rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold leading-tight text-violet-700">
              추가교환
            </span>
            {c.settledBy === 'inventory' ? (
              <span
                className="inline-flex rounded-full bg-teal-100 px-1.5 py-0.5 text-[9px] font-semibold leading-tight text-teal-700"
                title="기구매 미전달 쿠폰으로 대체발송 완료"
              >
                재고충당
              </span>
            ) : c.settledBy === 'extraPayment' || c.extraPayment?.isPaid ? (
              <span
                className="inline-flex rounded-full bg-blue-100 px-1.5 py-0.5 text-[9px] font-semibold leading-tight text-blue-700"
                title="추가결제 완료"
              >
                추가결제
              </span>
            ) : (
              <span
                className="inline-flex rounded-full bg-red-100 px-1.5 py-0.5 text-[9px] font-bold leading-tight text-red-700"
                title="아직 추가결제/대체발송 처리 전 — 발송대기에 뜨지 않습니다"
              >
                미정산
              </span>
            )}
          </div>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
      <td className="max-w-[120px] whitespace-nowrap px-3 py-1.5">
        {embed ? (
          <span className="block truncate font-medium text-gray-800" title={embed.name}>
            {embed.name}
          </span>
        ) : (
          <span className="text-gray-300">-</span>
        )}
        {/* 선택형 상품의 실제 발송/선택 상품 — 대리발송 기록 확인용으로도 쓰인다 */}
        {c.selectedChoiceUnit && typeof c.selectedChoiceUnit === 'object' && (
          <span
            className="block truncate text-[10px] text-indigo-500"
            title={`선택상품: ${c.selectedChoiceUnit.name}`}
          >
            {c.selectedChoiceUnit.name}
          </span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
        {embed ? `${formatPrice(embed.priceRetail)}원` : '-'}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 font-mono font-medium text-gray-800">
        <button
          type="button"
          onClick={() => copyAdminPreviewLink('coupon', c.code)}
          title="클릭 시 관리자 확인용(비기록) 링크 복사"
          className="underline decoration-dotted underline-offset-2 hover:text-indigo-600 active:opacity-70"
        >
          {c.code}
        </button>
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">
        <span
          className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
            redeemStatusColor[c.redeemStatus]
          }`}
        >
          {redeemStatusLabel[c.redeemStatus]}
        </span>
        {c.proxyDelivery && (
          <span
            className="ml-1 inline-flex rounded-full bg-rose-100 px-1.5 py-0.5 text-[9px] font-semibold leading-tight text-rose-700"
            title="관리자 대리발송 — 수령자 신청(번호인증·서명) 없이 관리자가 직접 발송 처리한 건"
          >
            대리발송
          </span>
        )}
        {isDisposed && c.disposalReason && (
          <p
            className="mt-0.5 max-w-[100px] truncate text-[9px] text-slate-400"
            title={c.disposalReason}
          >
            {c.disposalReason}
          </p>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">
        {onResetShareStatus &&
        c.shareStatus !== GiftCouponShareStatus.NotShared &&
        c.redeemStatus !== GiftCouponRedeemStatus.Delivered ? (
          <button
            type="button"
            onClick={() => onResetShareStatus(c._id)}
            disabled={isResettingShare}
            title="클릭 시 미전달(NotShared) + 교환가능(Available)으로 되돌리기"
            className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight underline decoration-dotted underline-offset-2 active:opacity-70 disabled:opacity-50 ${
              shareStatusColor[c.shareStatus]
            }`}
          >
            {shareStatusLabel[c.shareStatus]}
          </button>
        ) : (
          <span
            className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-tight ${
              shareStatusColor[c.shareStatus]
            }`}
          >
            {shareStatusLabel[c.shareStatus]}
          </span>
        )}
      </td>
      {isSpawn ? (
        <td
          colSpan={2}
          className="whitespace-nowrap px-3 py-1.5"
          title="중복 공유로 추가 생성된 스폰 쿠폰 — 원본 쿠폰에서 파생됨 (카톡공유/공유채널 없음)"
        >
          {spawnOrigin ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700">
              중복원본 #{spawnOrigin.seq} · {spawnOrigin.code}
            </span>
          ) : (
            <span className="text-[10px] text-violet-400">중복생성 (원본 미상)</span>
          )}
        </td>
      ) : (
        <>
          <td className="whitespace-nowrap px-3 py-1.5">
            {c.shareKakaoCount && c.shareKakaoCount > 0 ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onOpenKakaoShareCallbacks?.(c)}
                  title={
                    c.shareKakaoCount >= 2
                      ? `⚠ 중복노출 — 이 쿠폰이 ${c.shareKakaoCount}개 서로 다른 카톡방으로 전달됨 (클릭 시 상세 이력)`
                      : `카카오톡 ${c.shareKakaoCount}개 채팅방에 전달됨 — 클릭 시 상세 이력`
                  }
                  className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold active:opacity-80 ${
                    c.shareKakaoCount >= 2
                      ? 'bg-red-500 text-white ring-1 ring-red-600'
                      : 'bg-kakao-yellow text-kakao-brown'
                  }`}
                >
                  {c.shareKakaoCount}회
                </button>
                {isCrossCouponOverlap && (
                  <span
                    className="inline-flex items-center rounded bg-red-100 px-1 py-0.5 text-[9px] font-bold text-red-700"
                    title="다건수신 — 같은 주문의 다른 쿠폰과 동일한 카톡방으로 전달됨"
                  >
                    다건수신
                  </span>
                )}
              </div>
            ) : isCrossCouponOverlap ? (
              <span
                className="inline-flex items-center rounded bg-red-100 px-1 py-0.5 text-[9px] font-bold text-red-700"
                title="다건수신 — 같은 주문의 다른 쿠폰과 동일한 카톡방으로 전달됨"
              >
                다건수신
              </span>
            ) : (
              <span className="text-gray-300">-</span>
            )}
          </td>
          <td className="whitespace-nowrap px-3 py-1.5">
            {c.lastSharedChannel ? (
              <span className="inline-flex items-center gap-1 text-gray-700">
                <ShareChannelIcon channel={c.lastSharedChannel} className="h-3.5 w-3.5" />
                <span>
                  {shareChannelLabel[c.lastSharedChannel] ?? c.lastSharedChannel}
                </span>
              </span>
            ) : (
              <span className="text-gray-300">-</span>
            )}
          </td>
        </>
      )}
      {onOpenExtraRedeem && (
        <td className="whitespace-nowrap px-3 py-1.5">
          {c.extraRedeem ? (
            <button
              type="button"
              onClick={() => onOpenExtraRedeem(c)}
              title="추가교환 열림 — 클릭 시 수정/닫기"
              className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700 hover:bg-violet-200"
            >
              잔여 {extraRedeemRemaining(c)}/{c.extraRedeem.allowed}
            </button>
          ) : isExtraRedeemEligible(c, { crossCouponOverlap: isCrossCouponOverlap }) ? (
            <button
              type="button"
              onClick={() => onOpenExtraRedeem(c)}
              title="이 쿠폰에 추가 교환을 열기 (중복 공유 확인 건은 교환신청 전이라도 가능)"
              className="inline-flex items-center rounded border border-violet-300 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700 hover:bg-violet-100"
            >
              허용
            </button>
          ) : isSpawn && onSpawnCancel ? (
            <button
              type="button"
              onClick={() => onSpawnCancel(c._id)}
              disabled={cancelingSpawnId === c._id}
              title="추가교환으로 만든 쿠폰 취소 — 정산내역(재고충당 시 흡수한 쿠폰 / 추가결제 항목)도 함께 되돌립니다"
              className="inline-flex items-center rounded border border-red-300 bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-600 hover:bg-red-100 disabled:opacity-50"
            >
              {cancelingSpawnId === c._id ? '취소중…' : '취소'}
            </button>
          ) : (
            <span className="text-gray-300">-</span>
          )}
        </td>
      )}
      {!hideDates && (
        <>
          <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
            {fmt(c.purchasedAt)}
          </td>
          <td className="whitespace-nowrap px-3 py-1.5">
            {c.redeemStatus === GiftCouponRedeemStatus.Delivered &&
            c.gifticon?.deliveredAt ? (
              <span className="font-medium text-green-600">
                {fmt(c.gifticon.deliveredAt)}
              </span>
            ) : (
              <span className="text-gray-500">{fmt(c.expiresAt)}</span>
            )}
          </td>
        </>
      )}
      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
        {c.sharedAt ? fmt(c.sharedAt) : '-'}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
        {c.viewedAt ? fmtTime(c.viewedAt) : '-'}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-gray-700">
        {c.recipient ? (
          (() => {
            const isPendingLater =
              c.redeemStatus === GiftCouponRedeemStatus.Available &&
              !!c.recipient.phoneNumber;
            return (
              <span>
                {c.recipient.name}{' '}
                <span
                  className={
                    isPendingLater ? 'font-semibold text-purple-600' : 'text-gray-400'
                  }
                  title={
                    isPendingLater
                      ? '나중에 받기 — 교환신청은 안 했지만 수신번호 인증까지 마친 쿠폰'
                      : undefined
                  }
                >
                  {fmtPhone(c.recipient.phoneNumber)}
                </span>
                {isPendingLater && (
                  <span
                    className="ml-1 inline-flex items-center rounded-full bg-purple-100 px-1.5 py-0.5 text-[9px] font-bold text-purple-700"
                    title="나중에 받기 — 교환신청은 안 했지만 수신번호 인증까지 마친 쿠폰"
                  >
                    나중
                  </span>
                )}
              </span>
            );
          })()
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
        {c.recipient ? fmtTime(c.recipient.requestedAt) : '-'}
      </td>
      <td className="max-w-[150px] px-3 py-1.5">
        {c.memo ? (
          <span className="block truncate text-gray-700" title={c.memo}>
            {c.memo}
          </span>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
      {showCsMemo && (
        <td className="whitespace-nowrap px-3 py-1.5">
          <CsMemoButton
            target={{ giftCoupon: c._id }}
            title={`쿠폰 ${c.code}-${c.seq} · CS 응대 메모`}
            subtitle={csMemoSubtitle}
            memos={csMemos ?? []}
            isLoading={csMemoLoading}
            unavailable={csMemoUnavailable}
          />
        </td>
      )}
      {showSpawnActions ? (
        <td colSpan={2} className="whitespace-nowrap px-3 py-1.5">
          <div className="flex items-center gap-1.5">
            {onSpawnPay && (
              <button
                type="button"
                onClick={() => onSpawnPay(c)}
                title="추가결제(입금) 후 발송대기로 이동"
                className="rounded bg-indigo-600 px-2 py-0.5 text-[10px] font-bold text-white hover:bg-indigo-700"
              >
                입금
              </button>
            )}
            {onSpawnFulfill && (
              <button
                type="button"
                onClick={() => onSpawnFulfill(c)}
                title="기구매 미전달 쿠폰으로 대체발송"
                className="rounded border border-emerald-500 px-2 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-50"
              >
                대체발송
              </button>
            )}
          </div>
        </td>
      ) : (
        <>
          <td className="whitespace-nowrap px-3 py-1.5 text-kakao-brown">
            {c.kakaoSharedAt ? (
              fmtTime(c.kakaoSharedAt)
            ) : (
              <span className="text-gray-300">-</span>
            )}
          </td>
          <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
            {c.confirmedAt ? fmtTime(c.confirmedAt) : '-'}
          </td>
        </>
      )}
      <td className="whitespace-nowrap px-3 py-1.5">
        {onUpdateAdditionalPhone ? (
          <AdditionalPhoneCell
            couponId={c._id}
            initialPhone={c.additionalRecipientPhone}
            onUpdate={onUpdateAdditionalPhone}
          />
        ) : c.additionalRecipientPhone ? (
          <div>
            <span className="text-xs text-amber-700">
              {fmtPhone(c.additionalRecipientPhone)}
            </span>
            <p className="text-[9px] text-amber-500">확인번호</p>
          </div>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">
        {c.shareHistory && c.shareHistory.length > 0 ? (
          <button
            type="button"
            onClick={() => onOpenShareHistory(c)}
            title={`재공유 이력 ${c.shareHistory.length}회 — 클릭 시 상세 보기`}
            className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 hover:bg-indigo-100 active:opacity-80"
          >
            {c.shareHistory.length}회
          </button>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">
        {c.issued?.type === 'refund' ? (
          <div className="flex items-center gap-1">
            <span className="inline-flex rounded-full bg-orange-100 px-1.5 py-0.5 text-[9px] font-semibold text-orange-700">
              환불 ·{' '}
              {c.refund?.refundedAt
                ? '완료'
                : c.issued.requestedAt
                ? '신청완료'
                : '링크발행'}
            </span>
            {c.refund?.amount && (
              <span className="text-[9px] text-red-600">
                {c.refund.amount.toLocaleString()}원
              </span>
            )}
            {onCancelIssue && !c.issued.requestedAt && (
              <button
                onClick={() => onCancelIssue(c._id)}
                disabled={isIssuing}
                className="text-[9px] text-red-500 hover:underline disabled:opacity-50"
              >
                취소
              </button>
            )}
          </div>
        ) : c.issued?.type === 'renewal' ? (
          <div className="flex items-center gap-1">
            <span className="inline-flex rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-semibold text-violet-700">
              연장 ·{' '}
              {c.isRenewed ? '완료' : c.issued.requestedAt ? '신청완료' : '링크발행'}
            </span>
            {onCancelIssue && !c.issued.requestedAt && !c.isRenewed && (
              <button
                onClick={() => onCancelIssue(c._id)}
                disabled={isIssuing}
                className="text-[9px] text-red-500 hover:underline disabled:opacity-50"
              >
                취소
              </button>
            )}
          </div>
        ) : (onIssueRefund || onIssueRenewal) &&
          !c.refund &&
          c.redeemStatus !== GiftCouponRedeemStatus.Canceled &&
          c.redeemStatus !== GiftCouponRedeemStatus.Disposed &&
          c.redeemStatus !== GiftCouponRedeemStatus.Refunded ? (
          <div className="flex items-center gap-1">
            {onIssueRefund && (
              <button
                onClick={() => onIssueRefund(c._id)}
                disabled={isIssuing}
                className="rounded bg-orange-50 px-2 py-0.5 text-[10px] font-semibold text-orange-700 hover:bg-orange-100 disabled:opacity-50"
              >
                환불
              </button>
            )}
            {onIssueRefund &&
              onIssueRenewal &&
              c.redeemStatus === GiftCouponRedeemStatus.Delivered &&
              !c.isRenewed &&
              !c.isRenewal && <span className="text-gray-300">|</span>}
            {onIssueRenewal &&
              c.redeemStatus === GiftCouponRedeemStatus.Delivered &&
              !c.isRenewed &&
              !c.isRenewal && (
                <button
                  onClick={() => onIssueRenewal(c._id)}
                  disabled={isIssuing}
                  className="rounded bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700 hover:bg-violet-100 disabled:opacity-50"
                >
                  연장
                </button>
              )}
          </div>
        ) : (
          <span className="text-gray-300">-</span>
        )}
      </td>
    </tr>
  );
};

/**
 * "수신번호"(확인번호 = additionalRecipientPhone) 인라인 편집 셀.
 * 다량 수정을 위해 저장해도 새로고침하지 않고, 저장값을 로컬에 낙관적으로 유지한다.
 */
const AdditionalPhoneCell: FC<{
  couponId: string;
  initialPhone?: string;
  onUpdate: (couponId: string, newPhone: string) => Promise<boolean>;
}> = ({ couponId, initialPhone, onUpdate }) => {
  // 저장 성공 시 새로고침 없이 표시값을 갱신하기 위한 로컬 상태.
  const [phone, setPhone] = useState(initialPhone ?? '');
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = useCallback(() => {
    setEditValue(phone.replace(/[^0-9]/g, ''));
    setIsEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [phone]);

  const cancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditValue('');
  }, []);

  const confirmEdit = useCallback(async () => {
    const cleaned = editValue.replace(/[^0-9]/g, '');
    if (cleaned === phone.replace(/[^0-9]/g, '')) {
      cancelEdit();
      return;
    }
    setIsSaving(true);
    const success = await onUpdate(couponId, cleaned);
    setIsSaving(false);
    if (success) {
      setPhone(cleaned);
      setIsEditing(false);
      setEditValue('');
    }
  }, [editValue, phone, onUpdate, couponId, cancelEdit]);

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <input
          ref={inputRef}
          type="tel"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value.replace(/[^0-9]/g, ''))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') confirmEdit();
            if (e.key === 'Escape') cancelEdit();
          }}
          disabled={isSaving}
          maxLength={11}
          placeholder="01012345678"
          className="w-[104px] rounded border border-amber-300 px-1.5 py-0.5 text-xs focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
        />
        <button
          type="button"
          onClick={confirmEdit}
          disabled={isSaving}
          className="rounded bg-amber-600 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-amber-700 disabled:opacity-50"
        >
          {isSaving ? '...' : '저장'}
        </button>
        <button
          type="button"
          onClick={cancelEdit}
          disabled={isSaving}
          className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 hover:bg-gray-300 disabled:opacity-50"
        >
          취소
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      title="클릭하여 신청자(확인) 번호 입력/수정"
      className="group flex flex-col items-start rounded px-1 py-0.5 hover:bg-amber-50"
    >
      {phone ? (
        <>
          <span className="text-xs text-amber-700 group-hover:underline">
            {fmtPhone(phone)}
          </span>
          <span className="text-[9px] text-amber-500">확인번호 · 수정</span>
        </>
      ) : (
        <span className="text-xs text-amber-600 group-hover:underline">+ 번호 입력</span>
      )}
    </button>
  );
};
